import { type IEvent, createSubscriber, getGlobalEventsLogs } from '@cian/events-log';
import { type IHttpApi } from '@cian/http-api';
import { type ILogger } from '@cian/logger';

import { useCallback } from 'react';

import { fetchUserSafe } from '@/app/shared/services/fetchUser';
import { fetchUserLkType } from '@/app/shared/services/fetchUserLkType';
import { type ApplicationContextCustom } from '@/app/shared/types/ApplicationContext';

import { TOPIC_USER, getUserConsumer } from '@/packages/utils/getUserConsumer';
import { noop } from '@/packages/utils/noop';
import { reloadHeader } from '@/packages/utils/reloadHeader';

import { useReduxDispatch } from '../redux-state/store';
import { userActions } from '../redux-state/store/slices/user';

export type RequestAuthenticationParameters = {
  alert?: 'favorites' | 'chats' | 'hide' | null;
  onUnknownEvent?(event: IEvent<unknown>): Promise<void> | void;
  onDecline?(event: IEvent<unknown>): Promise<void> | void;
  onSuccess?(event: IEvent<unknown>): Promise<void> | void;
};

type AuthenticateEvent = {
  autoRefresh?: boolean;
  alert?: 'favorites' | 'chats' | 'hide' | null;
};

type SubscribersHandlerHooks = {
  /**
   * @description Вызывается при любом событии
   */
  onUnknownEvent(event: IEvent<unknown>): Promise<void> | void;
  /**
   * @description Вызывается при авторизации
   */
  onAuthenticated(event: IEvent<unknown>): Promise<void> | void;
  /**
   * @description Вызывается при авторизации или закрытии модального окна
   */
  onSettled(event: IEvent<unknown>): Promise<void> | void;
  /**
   * @description Вызывается при закрытии модального окна
   */
  onDecline(event: IEvent<unknown>): Promise<void> | void;
};

const subscribersHandlerFactory =
  (hooks: SubscribersHandlerHooks) =>
  async (event: IEvent<unknown>): Promise<void> => {
    const { onAuthenticated, onSettled, onDecline, onUnknownEvent } = hooks;

    if (event.type === 'authenticated') {
      await onAuthenticated(event);
      await onSettled(event);
    } else if (event.type === 'authentication_modal_closed') {
      await onDecline(event);
      await onSettled(event);
    } else {
      await onUnknownEvent(event);
    }
  };

type RequestAuthentication = (params?: RequestAuthenticationParameters) => void;

interface IApplicationContext {
  logger: ILogger;
  custom: Pick<ApplicationContextCustom, 'subdomain'>;
  httpApi: IHttpApi;
}

export function useRequestAuthentication(
  { logger, custom, httpApi }: IApplicationContext,
  parameters: RequestAuthenticationParameters = {},
): RequestAuthentication {
  const dispatch = useReduxDispatch();

  return useCallback<RequestAuthentication>(
    params => {
      const {
        onDecline = noop,
        onSuccess = noop,
        onUnknownEvent = noop,
        alert = null,
      }: RequestAuthenticationParameters = { ...parameters, ...params };

      const globalLog = getGlobalEventsLogs();
      const consumer = getUserConsumer();

      const userSubscriber = createSubscriber(
        subscribersHandlerFactory({
          async onAuthenticated(event) {
            reloadHeader(logger);

            const userPromise = fetchUserSafe({ logger, httpApi }, { subdomain: custom.subdomain });

            const [user, userLkType] = await Promise.all([
              userPromise,
              userPromise.then(userData => (userData.isAuthenticated ? fetchUserLkType({ logger, httpApi }) : null)),
            ]);

            dispatch(userActions.setUserData({ ...user, lkType: userLkType }));

            await onSuccess(event);
          },
          onDecline,
          onSettled() {
            consumer.unsubscribe(userSubscriber);
          },
          onUnknownEvent,
        }),
      );

      consumer.subscribe(userSubscriber);

      globalLog.produce<Required<AuthenticateEvent>>(TOPIC_USER, {
        type: 'authenticate',
        value: { autoRefresh: false, alert },
      });
    },
    [parameters, custom, dispatch, logger, httpApi],
  );
}
